import React, { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Separator } from "@/components/ui/separator";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import ImageUpload from "@/components/ui/image-upload";
import {
  Save,
  Eye,
  RefreshCw,
  FileText,
  Users,
  CheckCircle2,
  MessageSquare,
  ListChecks,
  Settings,
  Layout,
  Edit2,
  Copy,
  ChevronRight,
  ExternalLink,
  Sparkles,
  LayoutDashboard,
  Plus,
  Trash2,
  UserPlus,
  Mail,
  Crown,
  Shield,
  Image,
  Palette
} from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

export default function PilotOverrideEditor({ profileId, pilotName, templateId = "instructor_pilot" }) {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [templateContent, setTemplateContent] = useState({});
  const [overrides, setOverrides] = useState({});
  const [mergedContent, setMergedContent] = useState({});
  const [hosts, setHosts] = useState([]);
  const [activeTab, setActiveTab] = useState("overrides");
  const [showAddHostModal, setShowAddHostModal] = useState(false);
  const [newHostEmail, setNewHostEmail] = useState("");
  const [newHostRole, setNewHostRole] = useState("host");
  const [hasChanges, setHasChanges] = useState(false);

  useEffect(() => {
    loadData();
  }, [profileId]);

  const loadData = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem("session_token");
      
      // Load overrides and merged content
      const overridesRes = await fetch(`${API}/api/instructor-pilots/profiles/${profileId}/overrides`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (overridesRes.ok) {
        const data = await overridesRes.json();
        setTemplateContent(data.template_content || {});
        setOverrides(data.overrides || {});
        setMergedContent(data.merged_content || {});
      }
      
      // Load hosts
      const hostsRes = await fetch(`${API}/api/instructor-pilots/profiles/${profileId}/hosts`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (hostsRes.ok) {
        const data = await hostsRes.json();
        setHosts(data.hosts || []);
      }
    } catch (err) {
      console.error("Failed to load pilot data:", err);
      toast.error("Failed to load pilot data");
    } finally {
      setLoading(false);
    }
  };

  const saveOverrides = async () => {
    setSaving(true);
    try {
      const token = localStorage.getItem("session_token");
      const res = await fetch(`${API}/api/instructor-pilots/profiles/${profileId}/overrides`, {
        method: "PUT",
        headers: { 
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}` 
        },
        body: JSON.stringify({ overrides })
      });
      
      if (res.ok) {
        toast.success("Overrides saved!");
        setHasChanges(false);
        loadData(); // Reload to get updated merged content
      } else {
        toast.error("Failed to save overrides");
      }
    } catch (err) {
      toast.error("Error saving overrides");
    } finally {
      setSaving(false);
    }
  };

  const updateOverride = (path, value) => {
    setHasChanges(true);
    setOverrides(prev => {
      const newOverrides = { ...prev };
      if (!newOverrides.content) newOverrides.content = {};
      
      const keys = path.split(".");
      let current = newOverrides.content;
      for (let i = 0; i < keys.length - 1; i++) {
        if (!current[keys[i]]) current[keys[i]] = {};
        current = current[keys[i]];
      }
      current[keys[keys.length - 1]] = value;
      return newOverrides;
    });
  };

  const getOverrideValue = (path) => {
    const keys = path.split(".");
    let value = overrides?.content;
    for (const key of keys) {
      value = value?.[key];
    }
    return value;
  };

  const getTemplateValue = (path) => {
    const keys = path.split(".");
    let value = templateContent;
    for (const key of keys) {
      value = value?.[key];
    }
    return value;
  };

  const addHost = async () => {
    if (!newHostEmail) {
      toast.error("Please enter an email");
      return;
    }
    
    try {
      const token = localStorage.getItem("session_token");
      const res = await fetch(`${API}/api/instructor-pilots/profiles/${profileId}/hosts`, {
        method: "POST",
        headers: { 
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}` 
        },
        body: JSON.stringify({ email: newHostEmail, role: newHostRole })
      });
      
      if (res.ok) {
        toast.success("Host added!");
        setShowAddHostModal(false);
        setNewHostEmail("");
        setNewHostRole("host");
        loadData();
      } else {
        toast.error("Failed to add host");
      }
    } catch (err) {
      toast.error("Error adding host");
    }
  };

  const removeHost = async (hostId) => {
    if (!confirm("Remove this host?")) return;
    
    try {
      const token = localStorage.getItem("session_token");
      const res = await fetch(`${API}/api/instructor-pilots/profiles/${profileId}/hosts/${hostId}`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (res.ok) {
        toast.success("Host removed");
        loadData();
      } else {
        toast.error("Failed to remove host");
      }
    } catch (err) {
      toast.error("Error removing host");
    }
  };

  const renderOverrideField = (label, path, placeholder) => {
    const templateValue = getTemplateValue(path);
    const overrideValue = getOverrideValue(path);
    const hasOverride = overrideValue !== undefined && overrideValue !== "";
    
    return (
      <div className="space-y-2 p-3 rounded-lg border bg-white">
        <div className="flex items-center justify-between">
          <Label className="text-sm font-medium">{label}</Label>
          {hasOverride ? (
            <Badge className="bg-violet-100 text-violet-700 text-xs">Overridden</Badge>
          ) : (
            <Badge variant="outline" className="text-xs">Using Template</Badge>
          )}
        </div>
        <Input
          value={overrideValue || ""}
          onChange={(e) => updateOverride(path, e.target.value)}
          placeholder={templateValue || placeholder}
          className={hasOverride ? "border-violet-300" : ""}
        />
        {templateValue && (
          <p className="text-xs text-gray-400">Template default: {templateValue}</p>
        )}
      </div>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-8">
        <RefreshCw className="w-6 h-6 animate-spin text-gray-400" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-bold flex items-center gap-2">
            <Settings className="w-5 h-5" />
            {pilotName} - Override Settings
          </h2>
          <p className="text-sm text-gray-500">
            Customize this pilot's content. Blank fields inherit from the Instructor Pilot template.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={loadData}>
            <RefreshCw className="w-4 h-4 mr-1" />
            Refresh
          </Button>
          <Button onClick={saveOverrides} disabled={saving || !hasChanges}>
            <Save className="w-4 h-4 mr-1" />
            {saving ? "Saving..." : "Save Overrides"}
          </Button>
        </div>
      </div>

      {hasChanges && (
        <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg text-sm text-amber-800">
          You have unsaved changes
        </div>
      )}

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="overrides" className="flex items-center gap-1">
            <Edit2 className="w-4 h-4" />
            Content Overrides
          </TabsTrigger>
          <TabsTrigger value="branding" className="flex items-center gap-1">
            <Image className="w-4 h-4" />
            Branding
          </TabsTrigger>
          <TabsTrigger value="hosts" className="flex items-center gap-1">
            <Users className="w-4 h-4" />
            Hosts ({hosts.length})
          </TabsTrigger>
          <TabsTrigger value="preview" className="flex items-center gap-1">
            <Eye className="w-4 h-4" />
            Preview
          </TabsTrigger>
        </TabsList>

        {/* CONTENT OVERRIDES TAB */}
        <TabsContent value="overrides" className="mt-6">
          <div className="grid grid-cols-2 gap-6">
            {/* Confirmation Page Overrides */}
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-base flex items-center gap-2">
                  <CheckCircle2 className="w-4 h-4 text-green-500" />
                  Confirmation Page
                </CardTitle>
                <CardDescription>Override confirmation page text</CardDescription>
              </CardHeader>
              <CardContent className="space-y-3">
                {renderOverrideField("Main Title", "confirmation.title", "You're In!")}
                {renderOverrideField("Subtitle", "confirmation.subtitle", "Your spot is confirmed")}
                {renderOverrideField("Instructor Section Title", "confirmation.instructor_section.title", "Instructor Dashboard")}
                {renderOverrideField("Resources Section Title", "confirmation.resources_section.title", "Resources")}
              </CardContent>
            </Card>

            {/* Intake Page Overrides */}
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-base flex items-center gap-2">
                  <FileText className="w-4 h-4 text-blue-500" />
                  Intake Page
                </CardTitle>
                <CardDescription>Override intake form text</CardDescription>
              </CardHeader>
              <CardContent className="space-y-3">
                {renderOverrideField("Page Title", "intake.title", "Complete Your Registration")}
                {renderOverrideField("Page Subtitle", "intake.subtitle", "Tell us about yourself")}
                {renderOverrideField("Submit Button", "intake.submit_button", "Complete Registration")}
                {renderOverrideField("Question Bank Title", "intake.question_bank_section.title", "Questions for the Instructor")}
              </CardContent>
            </Card>

            {/* Post-Survey Overrides */}
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-base flex items-center gap-2">
                  <MessageSquare className="w-4 h-4 text-violet-500" />
                  Post-Survey
                </CardTitle>
                <CardDescription>Override post-survey text</CardDescription>
              </CardHeader>
              <CardContent className="space-y-3">
                {renderOverrideField("Section Title", "confirmation.post_survey_section.title", "Share Your Feedback")}
                {renderOverrideField("Section Subtitle", "confirmation.post_survey_section.subtitle", "Help us improve")}
                {renderOverrideField("Submit Button", "confirmation.post_survey_section.submit_button", "Submit Feedback")}
              </CardContent>
            </Card>

            {/* Host Dashboard Overrides */}
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-base flex items-center gap-2">
                  <LayoutDashboard className="w-4 h-4 text-orange-500" />
                  Host Dashboard
                </CardTitle>
                <CardDescription>Override dashboard labels</CardDescription>
              </CardHeader>
              <CardContent className="space-y-3">
                {renderOverrideField("Overview Tab", "host_dashboard.tabs.overview", "Overview")}
                {renderOverrideField("People Tab", "host_dashboard.tabs.people", "People")}
                {renderOverrideField("Insights Tab", "host_dashboard.tabs.insights", "Insights")}
                {renderOverrideField("Question Bank Tab", "host_dashboard.tabs.question_bank", "Question Bank")}
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* BRANDING TAB */}
        <TabsContent value="branding" className="mt-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Palette className="w-5 h-5" />
                Branding Override for {pilotName}
              </CardTitle>
              <CardDescription>
                Override the default branding for this pilot. Leave fields blank to use the template default.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Cover Image / Background */}
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <Label className="text-sm font-medium">RSVP Background Image</Label>
                  {overrides?.branding?.cover_image ? (
                    <Badge className="bg-violet-100 text-violet-700 text-xs">Overridden</Badge>
                  ) : (
                    <Badge variant="outline" className="text-xs">Using Template</Badge>
                  )}
                </div>
                <ImageUpload
                  value={overrides?.branding?.cover_image}
                  onChange={(url) => {
                    setOverrides(prev => ({
                      ...prev,
                      branding: { ...(prev?.branding || {}), cover_image: url }
                    }));
                    setHasChanges(true);
                  }}
                  description="Custom background for this pilot's RSVP invite pages"
                  previewSize="large"
                  aspectRatio="banner"
                />
                {templateContent?.welcome?.background_image && (
                  <p className="text-xs text-gray-400">Template has a default background set</p>
                )}
              </div>

              <Separator />

              {/* Logo */}
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <Label className="text-sm font-medium">Event Logo</Label>
                  {overrides?.branding?.logo ? (
                    <Badge className="bg-violet-100 text-violet-700 text-xs">Overridden</Badge>
                  ) : (
                    <Badge variant="outline" className="text-xs">Using Template</Badge>
                  )}
                </div>
                <ImageUpload
                  value={overrides?.branding?.logo}
                  onChange={(url) => {
                    setOverrides(prev => ({
                      ...prev,
                      branding: { ...(prev?.branding || {}), logo: url }
                    }));
                    setHasChanges(true);
                  }}
                  description="Custom logo for envelope sticker and event branding"
                  previewSize="medium"
                  aspectRatio="square"
                />
                {templateContent?.welcome?.logo_image && (
                  <p className="text-xs text-gray-400">Template has a default logo set</p>
                )}
              </div>

              <Separator />

              {/* Accent Color */}
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <Label className="text-sm font-medium">Accent Color</Label>
                  {overrides?.branding?.background_color ? (
                    <Badge className="bg-violet-100 text-violet-700 text-xs">Overridden</Badge>
                  ) : (
                    <Badge variant="outline" className="text-xs">Using Template</Badge>
                  )}
                </div>
                <div className="flex gap-4 items-center">
                  <Input
                    value={overrides?.branding?.background_color || ""}
                    onChange={(e) => {
                      setOverrides(prev => ({
                        ...prev,
                        branding: { ...(prev?.branding || {}), background_color: e.target.value }
                      }));
                      setHasChanges(true);
                    }}
                    placeholder={templateContent?.branding?.background_color || "#000000"}
                    className={`w-32 ${overrides?.branding?.background_color ? "border-violet-300" : ""}`}
                  />
                  {overrides?.branding?.background_color && (
                    <div 
                      className="w-10 h-10 rounded-lg border"
                      style={{ backgroundColor: overrides.branding.background_color }}
                    />
                  )}
                </div>
              </div>

              <div className="p-4 bg-blue-50 rounded-xl text-sm text-blue-800">
                <strong>Note:</strong> These branding settings will apply to all events under this pilot.
                Individual event hosts can further override these in their Host Dashboard.
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* HOSTS TAB */}
        <TabsContent value="hosts" className="mt-6">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle>Pilot Hosts</CardTitle>
                  <CardDescription>Manage who has access to this pilot's dashboard</CardDescription>
                </div>
                <Button onClick={() => setShowAddHostModal(true)}>
                  <UserPlus className="w-4 h-4 mr-1" />
                  Add Host
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              {hosts.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  <Users className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                  <p className="font-medium">No hosts added yet</p>
                  <p className="text-sm">Add hosts to give them access to this pilot's dashboard</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {hosts.map((host, idx) => (
                    <div 
                      key={host.host_id || idx} 
                      className="flex items-center justify-between p-4 rounded-lg border bg-white"
                    >
                      <div className="flex items-center gap-3">
                        <Avatar>
                          <AvatarFallback className="bg-violet-100 text-violet-700">
                            {(host.name || host.email || "?").substring(0, 2).toUpperCase()}
                          </AvatarFallback>
                        </Avatar>
                        <div>
                          <p className="font-medium">{host.name || "-"}</p>
                          <p className="text-sm text-gray-500">{host.email}</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-3">
                        {host.role === "owner" ? (
                          <Badge className="bg-amber-100 text-amber-700">
                            <Crown className="w-3 h-3 mr-1" />
                            Owner
                          </Badge>
                        ) : host.role === "admin" ? (
                          <Badge className="bg-violet-100 text-violet-700">
                            <Shield className="w-3 h-3 mr-1" />
                            Admin
                          </Badge>
                        ) : (
                          <Badge variant="secondary">Host</Badge>
                        )}
                        {host.role !== "owner" && (
                          <Button 
                            variant="ghost" 
                            size="sm"
                            className="text-red-500 hover:text-red-600"
                            onClick={() => removeHost(host.host_id)}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* PREVIEW TAB */}
        <TabsContent value="preview" className="mt-6">
          <Card>
            <CardHeader>
              <CardTitle>Merged Content Preview</CardTitle>
              <CardDescription>This shows the final content after applying your overrides to the template</CardDescription>
            </CardHeader>
            <CardContent>
              <ScrollArea className="h-[400px]">
                <pre className="text-xs bg-gray-50 p-4 rounded-lg overflow-auto">
                  {JSON.stringify(mergedContent, null, 2)}
                </pre>
              </ScrollArea>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Add Host Modal */}
      <Dialog open={showAddHostModal} onOpenChange={setShowAddHostModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add Host</DialogTitle>
            <DialogDescription>
              Add a host to give them access to this pilot's dashboard
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Email Address</Label>
              <Input
                type="email"
                placeholder="host@example.com"
                value={newHostEmail}
                onChange={(e) => setNewHostEmail(e.target.value)}
              />
            </div>
            <div className="space-y-2">
              <Label>Role</Label>
              <Select value={newHostRole} onValueChange={setNewHostRole}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="host">Host - Can manage people, view analytics</SelectItem>
                  <SelectItem value="co-host">Co-Host - Limited permissions</SelectItem>
                  <SelectItem value="admin">Admin - Full access, can add others</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAddHostModal(false)}>Cancel</Button>
            <Button onClick={addHost}>Add Host</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
